import type { ReactNode, ComponentPropsWithoutRef, JSX } from 'react';
import { isNotNil } from '@wistia/type-guards';
import { styled, css } from 'styled-components';
import { lineClampCss } from '../../css/lineClampCss';

export const ellipsisStyle = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: block;
  word-break: break-all;

  /*
    Using feature detection (i.e., -webkit-named-image) we only target Safari browsers
    with this additional styling to prevent a bug that's exclusive to Safari.

    Note that Safari does not properly hide the truncated text for non-inline/inline-block
    elements so we need to apply display: inline to all the paragraphs in the description.
    see https://stackoverflow.com/a/70683084
  */
  @supports (background: - webkit-named-image(i)) {
    & > div > div:has(p) {
      margin-top: 16px;
    }

    & > div > div > p {
      display: inline;
    }
  }
`;

export type EllipsisComponentProps = {
  $lines: number | undefined;
};

const EllipsisComponent = styled.span<EllipsisComponentProps>`
  ${ellipsisStyle};
  ${({ $lines }) => {
    if (isNotNil($lines)) {
      return lineClampCss($lines);
    }
    return undefined;
  }}
`;

export type EllipsisProps = ComponentPropsWithoutRef<'span'> & {
  /**
   * The text that will be truncated with an ellipsis
   */
  children?: ReactNode;
  /**
   * The number that will be truncated with an ellipsis
   */
  lines?: number | undefined;
};

/**
 * Truncate text with an ellispsis visble at the end
 */
export const Ellipsis = ({ children, lines, ...props }: EllipsisProps): JSX.Element => (
  <EllipsisComponent
    $lines={lines}
    {...props}
  >
    {children}
  </EllipsisComponent>
);

Ellipsis.displayName = 'Ellipsis';
